<template>
    <div class="page">
        <div class="row">
            <div class="col-sm-12">
                <div class="page-title-box d-flex flex-column justify-content-between">
                    <h4 class="page-title">Inventory</h4>
                    <div class="d-flex flex-wrap flex-md-nowrap mt-3 filter-wrapper">
                        <div class="col-xs-12 col-sm-4 range-slider-wrapper">
                            <div class="px-1 pb-3">
                                <label for="vehicle-year-range">Year</label>
                                <range-slider
                                    v-if="years.length"
                                    id="vehicle-year-range"
                                    v-model="vehicle_year_range"
                                    :max="maxYear"
                                    :min="minYear"
                                    class="w-100"
                                    title="year range"
                                    @change="onChangeYearRanges"
                                />
                            </div>
                        </div>
                        <div class="col-xs-12 col-sm-4 range-slider-wrapper">
                            <div class="px-1 pb-3">
                                <label for="vehicle-mileage-range">Mileage</label>
                                <range-slider
                                    v-if="years.length"
                                    id="vehicle-mileage-range"
                                    v-model="vehicle_mileage_range"
                                    :locale-string="true"
                                    :max="500000"
                                    :min="1"
                                    class="w-100"
                                    title="mileage range"
                                    @change="onChangeMileageRanges"
                                />
                            </div>
                        </div>
                        <div class="col-xs-12 col-sm-4 range-slider-wrapper">
                            <div class="px-1 pb-3">
                                <label for="vehicle-price-range">Price</label>
                                <range-slider
                                    v-if="years.length"
                                    id="vehicle-price-range"
                                    v-model="vehicle_price_range"
                                    :locale-string="true"
                                    :max="200000"
                                    :min="1"
                                    class="w-100"
                                    title="price range"
                                    @change="onChangePriceRanges"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="d-flex flex-wrap flex-md-nowrap mt-3 filter-wrapper">
                        <select
                            v-model="vehicle_make_id"
                            class="mr-2 form-control"
                            title="make"
                            @change="onChangeMake"
                        >
                            <option :value="null">Make</option>
                            <option
                                v-for="make in makes"
                                :value="make.id"
                            >
                                {{ make.name }}
                            </option>
                        </select>
                        <select
                            v-model="vehicle_model_id"
                            class="mr-2 mt-1 mt-md-0 form-control"
                            title="model"
                            @change="onChangeModel"
                        >
                            <option :value="null">Model</option>
                            <option
                                v-for="model in models"
                                :value="model.id"
                            >
                                {{ model.name }}
                            </option>
                        </select>
                        <select
                            v-model="condition_id"
                            class="mr-2 mt-1 mt-md-0 form-control"
                            title="condition"
                            @change="onChangeCondition"
                        >
                            <option :value="null">New/Used</option>
                            <option
                                v-for="condition in conditions"
                                :value="condition.id"
                            >
                                {{ condition.name }}
                            </option>
                        </select>
                        <select
                            v-model="filter"
                            class="mr-2 mt-1 mt-md-0 form-control"
                            title="filter"
                            @change="onChangeFilter"
                        >
                            <option :value="null">Sort/Filter</option>
                            <option
                                v-for="filter in filters"
                                :value="filter.value"
                            >
                                {{ filter.name }}
                            </option>
                        </select>
                        <div class="input-group mt-1 mt-md-0">
                            <input
                                v-model="stock_id"
                                class="search-by-stock-id form-control"
                                placeholder="Stock Number"
                                type="text"
                            >
                            <div class="input-group-append">
                                <button
                                    class="btn btn-primary waves-effect waves-light"
                                    type="button"
                                    @click="searchByStockId"
                                >
                                    <i class="mdi mdi-magnify"/>
                                    Search
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="position-relative">
            <loading
                :active.sync="loadingVehicles"
                :height="30"
                :is-full-page="false"
                :width="30"/>
            <InventoryVehicles/>
        </div>
    </div>
</template>

<script>
import {debounce, get, includes, omit} from 'lodash'
import Getters from "@/constants/store/getters";
import {CLEAR_INVENTORY_VEHICLES, FETCH_INVENTORY_VEHICLES} from "@/store/modules/vehicle/actions";
import InventoryVehicles from "@/components/main/inventory/InventoryVehicles";
import RangeSlider from "@/components/custom/RangeSlider";
import store from '@/store';
import {FETCH_USERS} from "@/store/modules/user/actions";
import Actions from "@/constants/store/actions";
import {mapState} from "vuex";
import YearMixin from '@/mixins/year'

const namespace = 'vehicle'
export default {
    components: {
        InventoryVehicles,
        RangeSlider
    },
    mixins: [YearMixin],
    created() {
        this.fetchVehicles()
        if (!this.fetchedUsers) {
            this.fetchUsers()
        }
        this.initVehicleYears();
        this.initVehicleMakes();
    },
    beforeRouteLeave(to, from, next) {
        store.dispatch(`${namespace}/${CLEAR_INVENTORY_VEHICLES}`)
        next()
    },
    watch: {
        '$route.query'() {
            this.fetchVehicles()
        }
    },
    mounted() {
        this.vehicle_make_id = parseInt(get(this.$route, 'query.vehicle_make_id')) || null
        this.vehicle_model_id = parseInt(get(this.$route, 'query.vehicle_model_id')) || null
        this.condition_id = parseInt(get(this.$route, 'query.condition_id')) || null
        this.filter = get(this.$route, 'query.sort', null);

        if (get(this.$route, 'query.visibility', null)) {
            this.filter = parseInt(get(this.$route, 'query.visibility')) ? 'visible' : 'hidden';
        }

        this.stock_id = parseInt(get(this.$route, 'query.stock_id')) || null

        if (this.$route.query.vehicle_year_range) {
            this.vehicle_year_range = [this.$route.query.vehicle_year_range[0], this.$route.query.vehicle_year_range[1]]
        }
        if (this.$route.query.vehicle_mileage_range) {
            this.vehicle_mileage_range = [this.$route.query.vehicle_mileage_range[0], this.$route.query.vehicle_mileage_range[1]]
        }
        if (this.$route.query.vehicle_price_range) {
            this.vehicle_price_range = [this.$route.query.vehicle_price_range[0], this.$route.query.vehicle_price_range[1]]
        }
    },
    data() {
        return {
            filters: [
                {
                    name: 'Days in Stock (ASC)',
                    value: 'total_days_on_floor',
                },
                {
                    name: 'Days in Stock (DESC)',
                    value: '-total_days_on_floor',
                },
                {
                    name: 'Price (ASC)',
                    value: 'price',
                },
                {
                    name: 'Price (DESC)',
                    value: '-price',
                },
                {
                    name: 'Mileage (ASC)',
                    value: 'mileage',
                },
                {
                    name: 'Mileage (DESC)',
                    value: '-mileage',
                },
                {
                    name: 'Visible',
                    value: 'visible',
                },
                {
                    name: 'Hidden',
                    value: 'hidden',
                }
            ],
            vehicle_make_id: null,
            vehicle_model_id: null,
            selected_make: null,
            vehicle_year_range: [],
            vehicle_mileage_range: [1, 500000],
            vehicle_price_range: [1, 200000],
            condition_id: null,
            filter: null,
            stock_id: null,
        }
    },
    computed: {
        ...mapState('vehicle', ['inventoryVehicles']),
        ...mapState('userModule', ['users']),
        ...mapState('vehicle', {
            loadingVehicles: (state) => state.loading
        }),
        ...mapState('userModule', {
            fetchedUsers: (state) => state.fetched
        }),
        makes() {
            return this.$store.state.vehicle_makes.data;
        },
        models() {
            if (!this.selected_make) {
                return [];
            }
            return this.selected_make.vehicle_models;
        },
        conditions() {
            return this.$store.getters[Getters.get_conditions]
        },
    },
    methods: {
        fetchVehicles() {
            store.dispatch(`${namespace}/${FETCH_INVENTORY_VEHICLES}`, this.$route.query)
        },
        fetchUsers() {
            store.dispatch(`userModule/${FETCH_USERS}`, {no_pagination: 1})
        },
        initVehicleYears() {
            this.$store.dispatch(Actions.fetch_vehicle_years)
                .then(() => {
                    if (!this.$route.query.vehicle_year_range) {
                        this.vehicle_year_range = [
                            this.minYear,
                            this.maxYear,
                        ]
                    }
                })
        },
        initVehicleMakes() {
            if (this.$store.state.vehicle_makes.meta.fetched) {
                return;
            }
            this.$store.dispatch(Actions.fetch_vehicle_makes);
        },
        onChangeMake(e) {
            this.vehicle_model_id = null;
            this.selected_make = _.find(this.makes, {id: parseInt(e.target.value)});
            this.$router.push({
                name: this.$route.name,
                query: this.getQuery(),
            })
        },
        onChangeModel() {
            this.$router.push({
                name: this.$route.name,
                query: this.getQuery(),
            })
        },
        onChangeYearRanges: debounce(function () {
            this.$router.push({
                name: this.$route.name,
                query: this.getQuery(),
            })
        }, 300),
        onChangeMileageRanges: debounce(function () {
            this.$router.push({
                name: this.$route.name,
                query: this.getQuery(),
            })
        }, 300),
        onChangePriceRanges: debounce(function () {
            this.$router.push({
                name: this.$route.name,
                query: this.getQuery(),
            })
        }, 300),
        onChangeCondition() {
            this.$router.push({
                name: this.$route.name,
                query: this.getQuery(),
            })
        },
        onChangeFilter() {
            this.$router.push({
                name: this.$route.name,
                query: this.getQuery()
            })
        },
        searchByStockId() {
            this.$router.push({
                name: this.$route.name,
                query: this.getQuery()
            })
        },
        getQuery() {
            let query = {}
            if (this.vehicle_make_id) {
                query.vehicle_make_id = this.vehicle_make_id;
            }
            if (this.vehicle_model_id) {
                query.vehicle_model_id = this.vehicle_model_id;
            }

            query.vehicle_year_range = this.vehicle_year_range
            query.vehicle_mileage_range = this.vehicle_mileage_range
            query.vehicle_price_range = this.vehicle_price_range

            if (this.condition_id) {
                query.condition_id = this.condition_id;
            }
            if (includes(['total_days_on_floor', '-total_days_on_floor', 'price', '-price', 'mileage', '-mileage'], this.filter)) {
                query.sort = this.filter;
            }
            if (includes(['visible', 'hidden'], this.filter)) {
                query.visibility = this.filter === 'visible' ? 1 : 0;
            }
            if (this.stock_id) {
                query.stock_id = this.stock_id;
            }

            query = {
                ...omit(this.$route.query, this.$store.state.vehicle.queryKeys),
                ...query,
            }
            return query;
        },
    }
}
</script>

<style scoped>
.search-by-stock-id {
    width: 300px;
}

option:disabled {
    color: #c3c0c0;
}

@media (min-width: 768px) and (max-width: 1200px) {
    .filter-wrapper {
        flex-wrap: wrap !important;
    }

    .range-slider-wrapper {
        flex-basis: 32% !important;
        width: 32.5% !important;
        max-width: 32.5% !important;
    }

    .form-control {
        width: 23.5% !important;
        margin-top: .25rem !important;
    }

    .input-group {
        display: flex !important;
        align-items: self-end;
    }
}

@media (min-width: 320px) and (max-width: 480px) {
    select {
        margin-bottom: 0.25rem !important;
    }
}
</style>
